import { type ApiResponse } from "apisauce"

import { request } from "../../../services/api.ts"

const BASE_URL = "/user/branches"
const ACCESSIBLE_URL = "/user/branches-accessible"

const PATHS = {
  list: `${BASE_URL}/`,
  detail: (id: string) => `${BASE_URL}/${id}/`,
  archive: (id: string) => `${BASE_URL}/${id}/archive/`,
  restore: (id: string) => `${BASE_URL}/${id}/restore/`,
  archivePreview: (id: string) => `${BASE_URL}/${id}/archive_preview/`,
  configuration: (id: string) => `${BASE_URL}/${id}/configuration/`,
  team: (id: string) => `${BASE_URL}/${id}/team/`,
  setupStatus: `${BASE_URL}/setup-status/`,
  setupTarget: `${BASE_URL}/setup-target/`,
  legalEntities: `${BASE_URL}/legal-entities/`,
  accessible: `${ACCESSIBLE_URL}/`,
  companyDashboard: `${BASE_URL}/company-dashboard/`
}

export const getAccessibleBranches = (): Promise<ApiResponse<any>> => {
  return request.api.get(PATHS.accessible)
}

export const getBranches = (
  params?: Record<string, unknown>
): Promise<ApiResponse<any>> => {
  return request.api.get(PATHS.list, params)
}

export const getBranchDetail = (id: string): Promise<ApiResponse<any>> => {
  return request.api.get(PATHS.detail(id))
}

export const createBranch = (data: Record<string, unknown>): Promise<ApiResponse<any>> => {
  return request.api.post(PATHS.list, data)
}

export const updateBranch = (
  id: string,
  data: Record<string, unknown>
): Promise<ApiResponse<any>> => {
  return request.api.patch(PATHS.detail(id), data)
}

export const archiveBranch = (id: string): Promise<ApiResponse<any>> => {
  return request.api.post(PATHS.archive(id))
}

export const restoreBranch = (id: string): Promise<ApiResponse<any>> => {
  return request.api.post(PATHS.restore(id))
}

export const getArchivePreview = (id: string): Promise<ApiResponse<any>> => {
  return request.api.get(PATHS.archivePreview(id))
}

export const getBranchConfiguration = (id: string): Promise<ApiResponse<any>> => {
  return request.api.get(PATHS.configuration(id))
}

export const updateBranchConfiguration = (
  id: string,
  data: Record<string, unknown>
): Promise<ApiResponse<any>> => {
  return request.api.patch(PATHS.configuration(id), data)
}

export const getBranchTeam = (id: string): Promise<ApiResponse<any>> => {
  return request.api.get(PATHS.team(id))
}

export const assignBranchMember = (
  id: string,
  data: Record<string, unknown>
): Promise<ApiResponse<any>> => {
  return request.api.post(PATHS.team(id), data)
}

export const getSetupStatus = (): Promise<ApiResponse<any>> => {
  return request.api.get(PATHS.setupStatus)
}

export const updateSetupTarget = (target: number): Promise<ApiResponse<any>> => {
  return request.api.patch(PATHS.setupTarget, { target })
}

export const getLegalEntities = (): Promise<ApiResponse<any>> => {
  return request.api.get(PATHS.legalEntities)
}

// `scope` is either `{ all: true }` or `{ teams: string[] }` — the caller's
// validated multi-select company-overview scope; the server independently
// re-validates every id against the caller's real accessible teams.
export const getCompanyDashboard = (
  scope: { all: true } | { teams: string[] }
): Promise<ApiResponse<any>> => {
  const params =
    "all" in scope ? { all_branches: "true" } : { teams: scope.teams.join(",") }
  return request.api.get(PATHS.companyDashboard, params)
}
